import React, { useState, useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { Link, useHistory } from "react-router-dom";
import MultiInput from "../../components/MultiInput";
import InputErrorMessage from "../../components/InputErrorMessage";
import { validateCpf, validateCnpj } from "../../utils/validate";
import { api, getEstimatedBudget, getHasBalance, checkAcceptanceOfTermsAndPolicies } from "../../api";
import { formatCityName, formatCpfCnpj, currencyFormat } from "../../utils/format";
import { trackEvent } from "../../utils/analytics";
import Skeleton from "../../components/Skeleton";
import AoiUpload from "./components/AoiUpload";
import ConfirmationModal from "./components/ConfirmationModal";
import TermsAndPoliciesModal from "./components/TermsAndPoliciesModal";
import CriteriaList from "./components/CriteriaList";
import IntroVideo from "./components/IntroVideo";
import ExtratoDapModal from "./components/ExtratoDapModal";
import { useAmbient } from '../../hooks/ambient';
import DownloadCar from "./components/DownloadCar";
import AreaMap from "../../components/AreaMap";
import "./styles.scss";

const CONFIRMATION_MODAL = "CONFIRMATION_MODAL";
const VIDEO_TUTORIAL_MODAL = "VIDEO_TUTORIAL_MODAL";
const LOADING_EXECUTION = "LOADING_EXECUTION";
const LOADING_SAVE = "LOADING_SAVE";
const LOADING_CRITERIA = "LOADING_CRITERIA";
const TERMS_AND_POLICIES_MODAL = "TERMS_AND_POLICIES_MODAL";
const DAP_DATA_NASCIMENTO = "DAP_DATA_NASCIMENTO";

const NewAnalysis = ({ setError }) => {
  const history = useHistory();
  const { formatMessage, formatNumber } = useIntl();
  const [documents, setDocuments] = useState([]);
  const [invalidDocuments, setInvalidDocuments] = useState([]);
  const [aoiInfo, setAoiInfo] = useState(null);
  const [downloadCar, setDownloadCar] = useState(null);
  const [analysisBudget, setAnalysisBudget] = useState();
  const [hasBalance, setHasBalance] = useState(true);
  const [termsAndPoliciesData, setTermsAndPoliciesData] = useState(null);
  const [title, setTitle] = useState("");
  const [loadingStep, setLoadingStep] = useState(null);
  const [openModal, setOpenModal] = useState(null);
  const [availableCriteria, setAvailableCriteria] = useState([]);
  const { isEnterpriseEnv } = useAmbient();
  const [numberCar, setNumberCar] = useState(null);
  const [btnNumberCar, setBtnNumberCar] = useState(false);
  const [isFile, setIsFile] = useState(false);
  const [cpfDap, setCpfDap] = useState("");
  const [isPronaf, setIsPronaf] = useState(false);

  const handleExecute = async (saveOnly = false) => {
    setLoadingStep(saveOnly ? LOADING_SAVE : LOADING_EXECUTION);

    const analysisBody = {};

    if (aoiInfo || downloadCar) {
      if (aoiInfo && aoiInfo.B3Area) analysisBody.area_id = aoiInfo.B3Area.id;
      if (downloadCar && downloadCar.B3Area) analysisBody.area_id = downloadCar.B3Area.id;
    }
    if (documents.length > 0) analysisBody.proponente = documents;
    if (title.length > 0) analysisBody.nome = title;
    if (numberCar) analysisBody.numero_car = numberCar;
    
    const url = isEnterpriseEnv
      ? `/b3_consultas/${saveOnly ? "save_all_criterias_enterprise" : "run_analysis_enterprise"}`
      : `/b3_consultas/${saveOnly ? "save_all_criterias" : "run_analysis_all"}`

    await api
      .post(
        url,
        analysisBody
      )
      .then(res => {
        history.push(
          saveOnly ? "/history" : `/results/${res.data.consulta_id}`
        );
      })
      .catch(err => {
        const errorMessage = err.response?.data?.message;
        setError(
          errorMessage ? (
            <>
              {errorMessage}{" "}
              <Link to="/contact">
                {formatMessage({ id: "ir-para-contato" })}
              </Link>
            </>
          ) : (
            formatMessage({ id: "executar-analise-erro" })
          )
        );

        setOpenModal(null);
      });

    setLoadingStep(null);
  };

  const getAvailableCriteria = useCallback(async () => {
    setLoadingStep(LOADING_CRITERIA);
    const criteriaParams = {};

    if (documents.length) {
      criteriaParams.cpf_cnpj = documents.map(doc => doc.value);
    }

    if (numberCar) {
      criteriaParams.numero_car = numberCar;
    }

    if (aoiInfo || downloadCar) {
      if (aoiInfo && aoiInfo.B3Area) criteriaParams.geojson_simplificado = aoiInfo.B3Area.geojson_simplificado;
      if (downloadCar && downloadCar.B3Area) criteriaParams.geojson_simplificado = downloadCar.B3Area.geojson_simplificado;
    }

    if (criteriaParams.geojson_simplificado || criteriaParams.cpf_cnpj) {
      await api
        .post("/b3_criterios/list_available_criterias", criteriaParams)
        .then(res => {
          if (res.data.code && ![200, 201].includes(res.data.code)) {
            setError(res.data.message);
          } else {
            setAvailableCriteria(res.data.result);
          }
        })
        .catch(() => {
          setError(formatMessage({ id: "erro-api" }));
        });
    } else {
      setAvailableCriteria([]);
    }

    setLoadingStep(null);
  }, [documents, aoiInfo, downloadCar, numberCar, formatMessage, setError]);

  const isValidDap = (value) => {
    if (validateCpf(value)) {
      return value;
    }
  }

  const handleModalSave = dataNascimento => {
    const value = {
      value: cpfDap,
      dt_nascimento: dataNascimento,
      label: formatCpfCnpj(cpfDap) + " | " + dataNascimento
    }

    setDocuments((prevArray) => [...prevArray, value]);
    setCpfDap('');
  }

  const formatDocument = useCallback(
    unformattedValue => {
      const value = unformattedValue.replace(/[. /-]/g, "");
      if (isValidDap(value) && isPronaf === true) {
        setCpfDap(value);
        setOpenModal(DAP_DATA_NASCIMENTO);
      }

      if (!validateCpf(value) && !validateCnpj(value)) {
        setInvalidDocuments([...invalidDocuments, unformattedValue]);
        return null;
      }

      if (validateCnpj(value) || (validateCpf(value) && isPronaf === false)) {
        setDocuments((prevArray) => [...prevArray, value]);
        return {
          value,
          label: formatCpfCnpj(value)
        }
      };
    },
    [invalidDocuments, setInvalidDocuments, isPronaf]
  );

  const downloadShapeFile = () => {
    trackEvent("download Shapefile");
    const url = "https://buscar.terras.app/app/files/template_area.zip";
    window.open(url, "_blank");
  };

  const openSICARLink = () => {
    trackEvent("click SICAR link");
    const url = "http://car.gov.br/publico/imoveis/index";
    window.open(url, "_blank");
  };

  const downloadKml = () => {
    trackEvent("download Kml");
    const url = "https://buscar.terras.app/app/files/template_area.kml";
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (!isEnterpriseEnv)
      getEstimatedBudget()
        .then(res => {
          const budget = res.data.result.budget_value;
          setAnalysisBudget(budget);
          getHasBalance({ budget }).then(response =>
            setHasBalance(response?.data?.has_enough_balance)
          );
        })
        .catch(() => {
          setError(formatMessage({ id: "erro-api" }));
        });

    checkAcceptanceOfTermsAndPolicies()
      .then(res => {
        const { politica_privacidade, termos_de_uso } = res.data;

        if (politica_privacidade.accepted && termos_de_uso.accepted) {
          return;
        }

        setTermsAndPoliciesData(res.data);
        setOpenModal(TERMS_AND_POLICIES_MODAL);
      })
      .catch(() => {
        setError(formatMessage({ id: "erro-api" }));
      });
  }, [formatMessage, setError, isEnterpriseEnv]);

  useEffect(() => {
    getAvailableCriteria();
  }, [getAvailableCriteria]);

  const AnalysisButton = ({
    messageId,
    isLoading,
    saveOnly = false,
    className
  }) => {
    const isDisabled = isEnterpriseEnv ? (
      (!aoiInfo && !downloadCar && !documents.length) ||
      !!isLoading
    ) : (
      (!aoiInfo && !downloadCar && !documents.length) ||
      !!isLoading ||
      !analysisBudget ||
      !hasBalance
    );

    return (
      <button
        className={`button ${className} full-width${isLoading === LOADING_SAVE ? " is-loading" : ""
          }`}
        disabled={isDisabled}
        onClick={() => handleExecute(messageId === 'Salvar')}
      >
        {formatMessage({ id: messageId })}
      </button>
    )
  }

  const updateCar = update => {
    setNumberCar(update);
    setBtnNumberCar(true);
  }

  const validateCar = () => {
    const regex = /^[a-z]{2}-\d{7}-[a-z0-9]{32}$/i;
    let validade = regex.test(numberCar);
    if (validade) {
      setBtnNumberCar(false);
      downloadAndLinkCar();
    } else {
      setError(formatMessage({ id: "recibo-car-invalido" }));
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const downloadAndLinkCar = useCallback(async () => {
    setBtnNumberCar(false);
    const formData = new FormData();
    formData.set("numero_car", numberCar);

    await api
      .post("/b3_consultas/vincula_car_proposta_sem_captcha", formData, {
        headers: {
          "Content-Type": "application/json"
        }
      }).then(response => {
        setDownloadCar(response.data.result);
        if (response.data.result === undefined) {
          setError(formatMessage({ id: "recibo-car-erro" }));
          setTimeout(() => {
            setError(null);
          }, 5000);
        }
      });
  }, [numberCar, setError, formatMessage]);

  const removeReceipt = () => {
    setDownloadCar(null);
    setNumberCar(null);
  }

  const removeCarZip = () => {
    setAoiInfo(null);
    setIsFile(false);
  }

  return (
    <>
      <h1>{formatMessage({ id: "nova-consulta" })}</h1>

      <div className="content-box new-analysis">
        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <p>
              {isEnterpriseEnv ? (
                <>
                  {formatMessage(
                    { id: "mensagem-input-analise-empresarial" }
                  )}
                </>
              ) : (
                <>
                  {formatMessage(
                    { id: "mensagem-input-analise" },
                    {
                      analysisBudget: analysisBudget ? (
                        formatNumber(analysisBudget, currencyFormat)
                      ) : (
                        <Skeleton width="50" />
                      )
                    }
                  )}
                </>
              )}
            </p>
          </div>
          <div className="tile is-6 is-vertical input-info">
            <button
              className="button secondary is-small"
              onClick={() => setOpenModal(VIDEO_TUTORIAL_MODAL)}
            >
              {formatMessage({ id: "mensagem-intro-video" })}
            </button>
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className="field">
              <div className="control">
                {formatMessage({ id: "consulta-pronaf" })}
                <input
                  type="checkbox"
                  className="checkbox-dap"
                  id="pronaf-type"
                  name="pronaf-type"
                  value={isPronaf}
                  onChange={e => setIsPronaf(e.target.checked)}
                />{" "}
              </div>
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-pronaf" })}
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className={`field${documents.length ? " filled" : ""}`}>
              <label className="label">
                {formatMessage({ id: "Documento" }, { num: 2 })}
              </label>
              <div className="control">
                <MultiInput
                  id="report-document"
                  optionFormatter={formatDocument}
                  value={documents}
                  onChange={setDocuments}
                />
              </div>
              {invalidDocuments.length > 0 && (
                <InputErrorMessage
                  message={`${formatMessage(
                    { id: "erro-documento-invalido" },
                    { num: invalidDocuments.length }
                  )}: ${invalidDocuments.map(doc => `"${doc}"`).join(", ")}`}
                  onClear={() => setInvalidDocuments([])}
                />
              )}
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-analise-documento" })}
            <CriteriaList
              availableCriteria={availableCriteria}
              criteriaType="pessoa"
            />
          </div>
        </div>
        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <p className="secondary centered">
              {formatMessage({ id: "e-ou" })}
            </p>
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            {(aoiInfo || downloadCar) && (
              <AreaMap aoiInfo={aoiInfo} downloadCar={downloadCar} id="report-file" className="map" />
            )}
            {!aoiInfo && !downloadCar && (
              <>
                {!btnNumberCar && numberCar ? (
                  <>
                    <div id="report-file">
                      <div className="loader"></div>
                      {numberCar && `${formatMessage({ id: "Enviando" })} "${numberCar}"`}
                    </div>
                  </>
                ) : (
                  <>
                    {!isFile && (
                      <>
                        <DownloadCar updateCar={updateCar} />
                        <p className="secondary centered">
                          {formatMessage({ id: "ou" })}
                        </p>
                      </>
                    )}
                    <AoiUpload aoiInfo={aoiInfo} setAoiInfo={setAoiInfo} setIsFile={setIsFile} />
                  </>
                )}
              </>
            )}
          </div>
          <div className="tile is-6 is-vertical input-info">
            <>
              {!!btnNumberCar && numberCar && (
                <>
                  <br />
                  <p>
                    <button className="button secondary is-small"
                      onClick={validateCar}
                    > {formatMessage({ id: "vincular-recibo" })}
                    </button>
                  </p>
                </>
              )}
              {(aoiInfo || downloadCar) && (
                <>
                  {
                    !!aoiInfo && aoiInfo.Municipio && (
                      <p>
                        <span className="bold">
                          {formatMessage({ id: "municipio" })}:
                        </span>{" "}
                        {formatCityName(aoiInfo.Municipio)}
                      </p>
                    )
                  }
                  {!!aoiInfo && aoiInfo.B3Area && aoiInfo.B3Area.area_ha && (
                    <p>
                      <span className="bold">
                        {formatMessage({ id: "area" })}:
                      </span>
                      {` ${formatNumber(aoiInfo.B3Area.area_ha)} ha`}
                    </p>
                  )}
                  {!!aoiInfo && aoiInfo.B3Area && aoiInfo.B3Area.area_ha && (
                    <p>
                      <button
                        className="button secondary is-small"
                        onClick={() => removeCarZip()}
                      >
                        {formatMessage({ id: "remover-arquivo" })}
                      </button>
                    </p>
                  )}

                  {!!downloadCar && downloadCar.Municipio && (
                    <p>
                      <span className="bold">
                        {formatMessage({ id: "municipio" })}:
                      </span>{" "}
                      {formatCityName(downloadCar.Municipio)}
                    </p>
                  )}
                  {!!downloadCar && downloadCar.B3Area && downloadCar.B3Area.area_ha && (
                    <p>
                      <span className="bold">
                        {formatMessage({ id: "area" })}:
                      </span>
                      {` ${formatNumber(downloadCar.B3Area.area_ha)} ha`}
                    </p>
                  )}
                  {!!downloadCar && downloadCar.B3Area && downloadCar.B3Area.area_ha && (
                    <p>
                      <button
                        className="button secondary is-small"
                        onClick={() => removeReceipt()}
                      >
                        {formatMessage({ id: "remover-arquivo" })}
                      </button>
                    </p>
                  )}
                </>
              )}
              <>
                {!aoiInfo && !downloadCar && !numberCar && (
                  <>
                    <br />
                    <p>
                      {formatMessage({
                        id: "primeira-opcao-recibo"
                      })}
                    </p>
                    <li>
                      {formatMessage(
                        {
                          id: "primeira-opcao-recibo-formato"
                        }
                      )}
                    </li>
                    <li>
                      {formatMessage(
                        {
                          id: "segunda-opcao-recibo-formato"
                        }
                      )}
                    </li>
                    <br />
                    <p>
                      {formatMessage({
                        id: "segunda-opcao-formatos"
                      })}
                    </p>
                    <li>
                      {formatMessage(
                        {
                          id: "formato-shapefile"
                        },
                        {
                          link: (
                            <button
                              className="link-button"
                              onClick={downloadShapeFile}
                            >
                              {formatMessage({ id: "baixar-template" })}
                            </button>
                          )
                        }
                      )}
                    </li>
                    <li>
                      {formatMessage(
                        {
                          id: "formato-sicar"
                        },
                        {
                          link: (
                            <button className="link-button" onClick={openSICARLink}>
                              SICAR Nacional
                            </button>
                          )
                        }
                      )}
                    </li>
                    <li>
                      {formatMessage(
                        {
                          id: "formato-kml"
                        },
                        {
                          link: (
                            <button
                              className="link-button"
                              onClick={downloadKml}
                            >
                              {formatMessage({ id: "baixar-template" })}
                            </button>
                          )
                        }
                      )}
                    </li>
                    <br />
                    <p>{formatMessage({ id: "um-arquivo-por-consulta" })}</p>
                  </>
                )}
              </>

              <CriteriaList
                availableCriteria={availableCriteria}
                criteriaTypes={["area", "numero_car"]}
              />
            </>
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className="field">
              <label className="label">
                {formatMessage({ id: "nome-consulta" })}
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  id="report-title"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-nome-consulta" })}
          </div>
        </div>

        <div className="tile is-ancestor analysis-action-buttons">
          <div className="tile is-6 is-vertical">
            <AnalysisButton
              className="primary"
              isLoading={loadingStep}
              messageId="Executar"
            />

            {!hasBalance && (
              <InputErrorMessage
                message={formatMessage({ id: "saldo-insuficiente" })}
              />
            )}

            <AnalysisButton
              className="secondary"
              isLoading={loadingStep}
              messageId="Salvar"
            />
          </div>

          <div className="tile is-6 is-vertical input-info">
            <p>
              {formatMessage({ id: "ajuda-executar-analise-1" })}
              <span className="bold">
                {formatMessage({ id: "Executar" })}
              </span>. {formatMessage({ id: "ajuda-executar-analise-2" })}
            </p>
          </div>
        </div>
      </div>

      {openModal === CONFIRMATION_MODAL && (
        <ConfirmationModal
          analysisBudget={analysisBudget}
          onClose={() => setOpenModal(null)}
          onConfirmation={handleExecute}
        />
      )}

      {openModal === TERMS_AND_POLICIES_MODAL && (
        <TermsAndPoliciesModal
          data={termsAndPoliciesData}
          onClose={() => setOpenModal(null)}
        />
      )}

      {openModal === VIDEO_TUTORIAL_MODAL && (
        <IntroVideo onClose={() => setOpenModal(null)} />
      )}

      {openModal === DAP_DATA_NASCIMENTO && (
        <ExtratoDapModal
          onClose={() => setOpenModal(null)}
          onSave={handleModalSave}
          setError={setError}
        />
      )}
    </>
  );
};

export default NewAnalysis;
